/**
 * Admin Sessions Dashboard
 *
 * Lists every live session — both ACTIVE and ENDED — for administrators.
 * Each row exposes a "Force end" action (admin override of the creator-only
 * `sessions.end`) and a "View participants" drawer-modal that shows the
 * `SessionParticipant` rows joined to user identity.
 *
 * Auth: ADMIN only — non-admins get an inline 403 view rather than a redirect
 * so deep links surface the access state explicitly. The same pattern is used
 * by `admin-users.tsx`.
 */

import type { AppRouter } from '@polygraf/server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { Radio, Search, Shield, StopCircle, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Badge, Button, Modal, Spinner, TextField } from '@/components/ui'
import { usePageTitle } from '@/hooks/use-page-title'
import { useUserRole } from '@/hooks/use-user-role'
import { formatDate, relativeTime } from '@/lib/format-time'
import { trpc } from '@/trpc/client'

type AdminSessionRow = inferRouterOutputs<AppRouter>['sessions']['listAll'][number]
type ParticipantRow = inferRouterOutputs<AppRouter>['sessions']['participants'][number]

export default function AdminSessionsPage() {
	const { t, i18n } = useTranslation()
	const location = useLocation()
	const { isAdmin, isLoading: roleLoading, error: roleError } = useUserRole()
	// Title reflects what the page is *currently* showing, not the target page —
	// otherwise non-admins see "Sessions Dashboard" in the tab while staring at
	// the access-denied card.
	usePageTitle(!roleLoading && !isAdmin ? t('admin.accessDenied') : t('admin.sessions.title'))
	const [search, setSearch] = useState('')
	const [participantsModalSessionId, setParticipantsModalSessionId] = useState<string | null>(null)

	// Optimistic-end overrides — keep ids that the admin force-ended this
	// session so the row flips to ENDED in the table the moment the mutation
	// resolves, without waiting for the next refetch.
	const [optimisticEndedIds, setOptimisticEndedIds] = useState<Set<string>>(() => new Set())

	const listQuery = trpc.sessions.listAll.useQuery(undefined, {
		enabled: isAdmin,
		retry: false,
		refetchOnWindowFocus: true,
	})

	const utils = trpc.useUtils()

	const forceEndMutation = trpc.sessions.forceEnd.useMutation({
		onMutate: async ({ id }) => {
			setOptimisticEndedIds((prev) => {
				const next = new Set(prev)
				next.add(id)
				return next
			})
		},
		onSettled: async () => {
			await utils.sessions.listAll.invalidate()
		},
		onError: (_err, { id }) => {
			// Revert the optimistic flip; the row goes back to whatever the next
			// refetch reports.
			setOptimisticEndedIds((prev) => {
				const next = new Set(prev)
				next.delete(id)
				return next
			})
		},
	})

	if (roleLoading) {
		return (
			<div className="max-w-6xl mx-auto px-4 py-8 flex justify-center opacity-30">
				<Spinner size="lg" />
			</div>
		)
	}

	if (roleError) {
		// Anonymous users hit a 401 (UNAUTHORIZED) on users.me — redirect them
		// to the sign-in flow with returnTo so they land back here after auth.
		// Other errors fall through to the generic alert.
		const code = (roleError.data as { code?: string } | null | undefined)?.code
		if (code === 'UNAUTHORIZED') {
			const returnTo = `${location.pathname}${location.search}`
			return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />
		}
		return (
			<div className="max-w-6xl mx-auto px-4 py-8">
				<Alert tone="error">{t('admin.roleCheckFailed')}</Alert>
			</div>
		)
	}

	if (!isAdmin) {
		return (
			<div className="max-w-6xl mx-auto px-4 py-8">
				<div className="card-refined p-12 text-center">
					<Shield size={48} className="mx-auto mb-4 opacity-30" />
					<p className="text-lg font-medium">{t('admin.accessDenied')}</p>
					<p className="text-base-content/60 mt-2">{t('admin.adminOnly')}</p>
				</div>
			</div>
		)
	}

	if (listQuery.isLoading) {
		return (
			<div className="flex justify-center py-12 text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	if (listQuery.error) {
		return (
			<div className="max-w-6xl mx-auto px-4 py-8">
				<Alert tone="error">{listQuery.error.message}</Alert>
			</div>
		)
	}

	const allRows = listQuery.data ?? []
	const filtered = search
		? allRows.filter((row) => {
				const q = search.toLowerCase()
				return (
					row.title.toLowerCase().includes(q) ||
					row.createdBy.name.toLowerCase().includes(q) ||
					row.id.toLowerCase().includes(q)
				)
			})
		: allRows

	return (
		<div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">{t('admin.sessions.title')}</h1>
				<p className="text-base-content/60 mt-1">{t('admin.sessions.subtitle')}</p>
			</div>

			<TextField
				aria-label={t('admin.sessions.searchLabel')}
				placeholder={t('admin.sessions.searchPlaceholder')}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				leadingIcon={<Search size={18} />}
			/>

			{forceEndMutation.error && <Alert tone="error">{forceEndMutation.error.message}</Alert>}

			{filtered.length === 0 ? (
				<div className="card-refined p-12 text-center">
					<Radio size={40} className="mx-auto mb-3 opacity-30" />
					<p className="text-base-content/60">{t('admin.sessions.empty')}</p>
				</div>
			) : (
				<div className="card-refined overflow-x-auto">
					<table className="table w-full" aria-label={t('admin.sessions.title')}>
						<thead>
							<tr className="text-left text-xs uppercase tracking-wide text-base-content/60">
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.title')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.creator')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.status')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.started')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.ended')}
								</th>
								<th scope="col" className="px-4 py-3">
									{t('admin.sessions.columns.participants')}
								</th>
								<th scope="col" className="px-4 py-3 text-right">
									{t('admin.sessions.columns.actions')}
								</th>
							</tr>
						</thead>
						<tbody>
							{filtered.map((row) => (
								<SessionRow
									key={row.id}
									row={row}
									locale={i18n.language}
									isOptimisticEnded={optimisticEndedIds.has(row.id)}
									isForceEnding={
										forceEndMutation.isPending && forceEndMutation.variables?.id === row.id
									}
									onForceEnd={() => forceEndMutation.mutate({ id: row.id })}
									onViewParticipants={() => setParticipantsModalSessionId(row.id)}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}

			<ParticipantsModal
				sessionId={participantsModalSessionId}
				onClose={() => setParticipantsModalSessionId(null)}
				locale={i18n.language}
			/>
		</div>
	)
}

interface SessionRowProps {
	row: AdminSessionRow
	locale: string
	isOptimisticEnded: boolean
	isForceEnding: boolean
	onForceEnd: () => void
	onViewParticipants: () => void
}

function SessionRow({
	row,
	locale,
	isOptimisticEnded,
	isForceEnding,
	onForceEnd,
	onViewParticipants,
}: SessionRowProps) {
	const { t } = useTranslation()
	// Treat optimistic-ended rows as ENDED for status badge + button gating.
	const effectiveStatus = isOptimisticEnded ? 'ENDED' : row.status
	const isActive = effectiveStatus === 'ACTIVE'

	return (
		<tr className="border-t border-base-300">
			<td className="px-4 py-3 align-top">
				<div className="font-medium truncate max-w-[24ch]">{row.title}</div>
				<div className="text-xs text-base-content/50 truncate max-w-[24ch]">{row.id}</div>
			</td>
			<td className="px-4 py-3 align-top text-sm">{row.createdBy.name || '—'}</td>
			<td className="px-4 py-3 align-top">
				<Badge tone={isActive ? 'success' : 'neutral'}>
					{isActive ? t('admin.sessions.status.active') : t('admin.sessions.status.ended')}
				</Badge>
			</td>
			<td className="px-4 py-3 align-top text-sm">
				<div>{formatDate(row.startedAt, locale)}</div>
				<div className="text-xs text-base-content/50">{relativeTime(row.startedAt, locale)}</div>
			</td>
			<td className="px-4 py-3 align-top text-sm">
				{row.endedAt ? (
					<>
						<div>{formatDate(row.endedAt, locale)}</div>
						<div className="text-xs text-base-content/50">{relativeTime(row.endedAt, locale)}</div>
					</>
				) : (
					<span className="text-base-content/40">—</span>
				)}
			</td>
			<td className="px-4 py-3 align-top text-sm tabular-nums">{row.participantCount}</td>
			<td className="px-4 py-3 align-top">
				<div className="flex items-center justify-end gap-2 flex-wrap">
					<Button
						size="sm"
						variant="ghost"
						leadingIcon={<Users size={14} />}
						onClick={onViewParticipants}
					>
						{t('admin.sessions.viewParticipants')}
					</Button>
					<Button
						size="sm"
						variant="danger"
						leadingIcon={<StopCircle size={14} />}
						onClick={onForceEnd}
						disabled={!isActive}
						loading={isForceEnding}
					>
						{t('admin.sessions.forceEnd')}
					</Button>
				</div>
			</td>
		</tr>
	)
}

interface ParticipantsModalProps {
	sessionId: string | null
	onClose: () => void
	locale: string
}

function ParticipantsModal({ sessionId, onClose, locale }: ParticipantsModalProps) {
	const { t } = useTranslation()
	const open = sessionId !== null

	const query = trpc.sessions.participants.useQuery(
		// `sessionId` is gated by `enabled`, so the non-null assertion is safe.
		// biome-ignore lint/style/noNonNullAssertion: gated by `enabled: open`
		{ id: sessionId! },
		{ enabled: open, retry: false },
	)

	const sortedParticipants = useMemo(() => {
		// Already ordered ASC on the server; this is a defensive copy so we
		// don't mutate the cached query data.
		return query.data ? [...query.data] : []
	}, [query.data])

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t('admin.sessions.participantsTitle')}
			description={t('admin.sessions.participantsSubtitle')}
			closeLabel={t('common.close')}
			size="lg"
		>
			{query.isLoading ? (
				<div className="flex justify-center py-8">
					<Spinner size="md" />
				</div>
			) : query.error ? (
				<Alert tone="error">{query.error.message}</Alert>
			) : sortedParticipants.length === 0 ? (
				<div className="text-center py-8 text-base-content/60">
					{t('admin.sessions.noParticipants')}
				</div>
			) : (
				<ul className="space-y-2">
					{sortedParticipants.map((participant) => (
						<ParticipantItem key={participant.id} participant={participant} locale={locale} />
					))}
				</ul>
			)}
		</Modal>
	)
}

interface ParticipantItemProps {
	participant: ParticipantRow
	locale: string
}

function ParticipantItem({ participant, locale }: ParticipantItemProps) {
	const { t } = useTranslation()
	return (
		<li className="card-refined p-3 flex items-center gap-4">
			<div className="flex-1 min-w-0">
				<div className="font-medium truncate">{participant.user.name || participant.user.id}</div>
				<div className="text-xs text-base-content/60">
					{t('admin.sessions.joinedAt', {
						time: formatDate(participant.joinedAt, locale),
					})}
				</div>
			</div>
			<Badge tone={participant.role === 'WRITER' ? 'accent' : 'neutral'}>
				{participant.role === 'WRITER'
					? t('admin.sessions.roleWriter')
					: t('admin.sessions.roleBroadcaster')}
			</Badge>
			<Badge tone="neutral">{t(`roles.${participant.user.role.toLowerCase()}`)}</Badge>
		</li>
	)
}
